import React from 'react';
import './ErrorBoundary.css';

/**
 * Error Boundary to catch React component errors and prevent full app crash.
 * "Script error" often means cross-origin errors (e.g. Mapbox) where details are stripped.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, retryKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleRetry = () => {
    this.props.onRetry?.();
    this.setState((prev) => ({
      hasError: false,
      error: null,
      retryKey: prev.retryKey + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback, fallbackMessage } = this.props;
      const error = this.state.error;
      const message = error?.message || 'Script error';
      const isScriptError = message === 'Script error';

      if (typeof Fallback === 'function') {
        return <Fallback error={error} onRetry={this.handleRetry} />;
      }

      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <h3>Something went wrong</h3>
            <p className="error-boundary-message">
              {fallbackMessage || (
                isScriptError
                  ? 'A script failed to load. This can happen with the map or an external service. Try refreshing the page.'
                  : message
              )}
            </p>
            {process.env.NODE_ENV === 'development' && error && (
              <pre className="error-boundary-stack">{error.stack}</pre>
            )}
            <div className="error-boundary-actions">
              <button onClick={this.handleRetry} className="btn-retry">
                Try again
              </button>
              {this.props.onClose && (
                <button onClick={this.props.onClose} className="btn-close">
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <React.Fragment key={this.state.retryKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

export default ErrorBoundary;
